// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect } from "react-redux";
import React, { useCallback } from "react";
import localForage from "localforage";

import { ProfileMenu as StyledProfileMenu, MenuItem } from "../styles";
import Update from "./Update";
import { SubHeading } from "./styles";
import ViewAs from "./ViewAs";
import OrgMenu from "./OrgMenu";
import { HiddenInput } from "src/styles";
import { logout, setProfileImage } from "src/actions";
import {
  toggleProfileModal,
  toggleChangePasswordModal,
  togglePreferencesModal,
} from "src/actions/modal";
import { setOrgSettings } from "src/actions/orgSettings";
import {
  toggleCreateOrgModal,
  toggleCloneOrgModal,
  toggleAboutModal,
} from "src/actions/modal";
import NameOrEmail from "src/components/user/NameOrEmailText";
import Icon from "src/icons";
import { showView } from "src/actions";
import {
  getAllOrgs,
  getUserRole,
  getOrgName,
  getCurrentUserId,
  isCurrentUserUnifizeEmployee,
  getCurrentOrg,
  getLastOrg,
} from "src/reducers";
import type { Set as ImmutableSet } from "immutable";
import { toast } from "react-toastify";
import { getFileExtension, verifyImage } from "src/utils/file";
import useComponentPermission from "src/hooks/useComponentPermission";
import { componentPermissions } from "src/constants/roleManagement";
import useClickoutside from "src/hooks/useClickoutside";

import type { AppState, UID } from "src/types";

type Props = {
  uid: UID,
  orgTitle: string,
  orgs: ImmutableSet<number>,
  role: string,
  orgId: number,
  _setProfileImage: Function,
  outerRef: HTMLDivElement,
  handleClose: Function,
  _logout: Function,
  _toggleProfileModal: Function,
  _nativigateToSettings: Function,
  _setOrgSettings: Function,
  _toggleCreateOrgModal: Function,
  _toggleCloneOrgModal: Function,
  _showView: Function,
  _togglePreferencesModal: Function,
  _toggleChangePasswordModal: Function,
  _toggleAboutModal: Function,
  canCloneOrg: boolean,
};

const ProfileMenu = ({
  uid,
  orgTitle,
  _setProfileImage,
  outerRef,
  handleClose,
  _logout,
  orgs,
  role,
  orgId,
  _toggleProfileModal,
  _setOrgSettings,
  _toggleCreateOrgModal,
  _toggleCloneOrgModal,
  _toggleAboutModal,
  _showView,
  _toggleChangePasswordModal,
  _togglePreferencesModal,
  canCloneOrg,
}: Props) => {
  const handleFileChange = useCallback(
    (event: any) => {
      const file = event.target.files[0];
      const fileExtension = getFileExtension(file.name);

      if (verifyImage(fileExtension)) {
        _setProfileImage(file);
      } else {
        toast.error(i18n.t(k.ONLY_IMAGES_ARE_ALLOWED));
      }

      handleClose();
    },
    [_setProfileImage, handleClose],
  );

  const handleLogout = useCallback(
    (event: any) => {
      event.stopPropagation();
      _logout();
    },
    [logout],
  );

  useClickoutside({ outerRef, handleClose });

  const openModal = (modalFunction: Function) => {
    modalFunction({ open: true });
  };

  const handleRefresh = async () => {
    try {
      const orgId = sessionStorage.getItem("lastOrg");

      if (orgId) {
        // Clearing indexdb
        await localForage.setItem(`chatrooms-${orgId}`, JSON.stringify([]));
        await localForage.setItem(`lastFetched-${orgId}`, "");
      }
    } catch (error) {
      console.log(i18n.t(k.UNABLE_TO_CLEAR_DB), error);
    }
    window.location.reload();
  };

  const handleOrgSettings = useCallback(() => {
    _showView("orgSettings");
    _setOrgSettings("home-screen");
  }, [_showView, _setOrgSettings]);

  const showChangeOrg = orgs.count() > 1;

  const showViewAs = role === "phantom";

  const orgSettingsEnabled = useComponentPermission(
    componentPermissions.homeSettings,
  );

  return (
    <StyledProfileMenu data-cy="styled-profile-menu-div">
      <Update />
      <SubHeading>
        <NameOrEmail uid={uid} />
      </SubHeading>
      <ul>
        <MenuItem
          data-cy="toggle-profile-modal-btn"
          onClick={() => openModal(_toggleProfileModal)}
        >
          <Icon type="profile" />
          <span>{i18n.t(k.UPDATE_NAME)}</span>
        </MenuItem>
        <MenuItem onClick={(e) => e.stopPropagation()}>
          <HiddenInput>
            <input
              data-cy="file-input"
              type="file"
              id="profilePhoto"
              onChange={handleFileChange}
              accept="image/*"
              title=""
            />
          </HiddenInput>
          <Icon type="profileCircle" />
          <span>{i18n.t(k.CHANGE_PROFILE_PICTURE)}</span>
        </MenuItem>
        <MenuItem data-cy="handle-refresh-btn" onClick={handleRefresh}>
          <Icon type="refresh2" />
          <span>{i18n.t(k.REFRESH)}</span>
        </MenuItem>
        <MenuItem
          data-cy="toggle-password-modal-btn"
          onClick={() => openModal(_toggleChangePasswordModal)}
        >
          <Icon type="security" />
          <span>{i18n.t(k.CHANGE_PASSWORD1)}</span>
        </MenuItem>
        <MenuItem
          data-cy="toggle-about-modal-btn"
          onClick={() => _toggleAboutModal({ value: true })}
        >
          <Icon type="infoFilled" />
          <span>{i18n.t(k.ABOUT)} Unifize</span>
        </MenuItem>
        <MenuItem
          data-cy="toggle-preferences-modal-btn"
          onClick={() => openModal(_togglePreferencesModal)}
        >
          <Icon type="instantMix" />
          <span>{i18n.t(k.PREFERENCES)}</span>
        </MenuItem>
        <MenuItem data-cy="handle-logout-btn" onClick={handleLogout}>
          <Icon type="logOut" />
          <span>{i18n.t(k.LOGOUT)}</span>
        </MenuItem>
      </ul>
      <SubHeading>{orgTitle}</SubHeading>
      <ul>
        {orgSettingsEnabled && (
          <MenuItem
            data-cy="handle-org-settings-btn"
            onClick={handleOrgSettings}
          >
            <Icon type="settings" />
            <span style={{ lineHeight: "normal" }}>
              {i18n.t(k.ORG_SETTINGS1)}
            </span>
          </MenuItem>
        )}

        {/* Show org creation option only for Unifize Org */}
        {orgId === 1 && (
          <MenuItem
            data-cy="toggle-create-org-modal-btn"
            onClick={() => _toggleCreateOrgModal({ open: true })}
          >
            <Icon type="org" />
            <span>{i18n.t(k.CREATE_NEW_ORG)}</span>
          </MenuItem>
        )}

        {canCloneOrg && (
          <MenuItem
            data-cy="toggle-clone-org-modal-btn"
            onClick={() => _toggleCloneOrgModal({ open: true })}
          >
            <Icon type="copy" />
            <span>{i18n.t(k.CLONE_ORG)}</span>
          </MenuItem>
        )}

        {showChangeOrg && <OrgMenu />}

        {showViewAs && <ViewAs />}
      </ul>
    </StyledProfileMenu>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  uid: getCurrentUserId(app),
  orgTitle: getOrgName(app, parseInt(app.orgs.last, 10)),
  orgs: getAllOrgs(app),
  role: getUserRole(app),
  orgId: getLastOrg(app),
  canCloneOrg:
    isCurrentUserUnifizeEmployee(app) && getCurrentOrg(app).cloneable,
});

export default connect(mapStateToProps, {
  _toggleProfileModal: toggleProfileModal,
  _setProfileImage: setProfileImage,
  _logout: logout,
  _setOrgSettings: setOrgSettings,
  _toggleCreateOrgModal: toggleCreateOrgModal,
  _toggleCloneOrgModal: toggleCloneOrgModal,
  _showView: showView,
  _toggleChangePasswordModal: toggleChangePasswordModal,
  _toggleAboutModal: toggleAboutModal,
  _togglePreferencesModal: togglePreferencesModal,
})(ProfileMenu);
